import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatError } from "@/api/client";
import { toast } from "sonner";
import { ArrowLeft, Save, MoreHorizontal, Copy, Trash2, History, X, Eye } from "lucide-react";
import EditorJsEditor from "@/components/EditorJsEditor";
import { uploadSpaceMedia } from "@/lib/media";

export default function ArticleEditorPage() {
  const { spaceId, articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState("");
  const [contentAr, setContentAr] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [excerptAr, setExcerptAr] = useState("");
  const [activeLang, setActiveLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showVersions, setShowVersions] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [editorNonce, setEditorNonce] = useState(0);
  const editorFlushRef = useRef(null);

  const applyArticle = useCallback((data) => {
    setArticle(data);
    setContent(data.content || "");
    const ar = (data.i18n || {}).ar || {};
    setTitleAr(ar.title || "");
    setContentAr(ar.content || "");
    setExcerptAr(ar.excerpt || "");
    setTagInput((data.tags || []).join(", "));
  }, []);

  const load = async () => {
    const [a, c] = await Promise.all([
      api.get(`/spaces/${spaceId}/articles/${articleId}`),
      api.get(`/spaces/${spaceId}/categories`),
    ]);
    applyArticle(a.data);
    setCategories(c.data);
  };

  useEffect(() => { load(); }, [spaceId, articleId]);

  const flushEditor = async () => {
    if (!editorFlushRef.current) return null;
    return editorFlushRef.current();
  };

  const switchLang = async (lang) => {
    if (lang === activeLang) return;
    const flushed = await flushEditor();
    if (flushed != null) {
      if (activeLang === "en") setContent(flushed);
      else setContentAr(flushed);
    }
    setActiveLang(lang);
  };

  const setField = (k, v) => { setArticle({ ...article, [k]: v }); setDirty(true); };

  const save = async ({ publish, unpublish } = {}) => {
    setSaving(true);
    try {
      const flushed = await flushEditor();
      let latestContent = content;
      let latestContentAr = contentAr;
      if (flushed != null) {
        if (activeLang === "en") latestContent = flushed;
        else latestContentAr = flushed;
      }
      const payload = {
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt,
        category_id: article.category_id || null,
        featured_image_url: article.featured_image_url || "",
        tags: tagInput.split(",").map((t) => t.trim()).filter(Boolean),
        content: latestContent,
        seo: article.seo,
        status: publish ? "published" : unpublish ? "draft" : article.status,
        i18n: {
          ar: { title: titleAr, content: latestContentAr, excerpt: excerptAr },
        },
      };
      const { data } = await api.put(`/spaces/${spaceId}/articles/${articleId}`, payload);
      applyArticle(data);
      setEditorNonce((n) => n + 1);
      setDirty(false);
      toast.success(publish ? "Published" : unpublish ? "Unpublished" : "Saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const copyEnToAr = async () => {
    if (!confirm("Replace Arabic content with the current English version? You can then translate it.")) return;
    const flushed = await flushEditor();
    const latestEn = flushed != null && activeLang === "en" ? flushed : content;
    if (flushed != null && activeLang === "ar") setContentAr(flushed);
    setTitleAr(article.title);
    setContentAr(latestEn);
    setExcerptAr(article.excerpt || "");
    setDirty(true);
    toast.success("Copied from English. Now translate.");
  };

  const onImageUpload = async (file) => {
    try {
      return await uploadSpaceMedia(spaceId, file);
    } catch (e) {
      toast.error(formatError(e));
      throw e;
    }
  };

  const duplicate = async () => {
    const { data } = await api.post(`/spaces/${spaceId}/articles/${articleId}/duplicate`);
    toast.success("Duplicated");
    navigate(`/spaces/${spaceId}/articles/${data.id}`);
  };

  const deleteArticle = async () => {
    if (!confirm("Delete this article?")) return;
    await api.delete(`/spaces/${spaceId}/articles/${articleId}`);
    toast.success("Deleted");
    navigate(`/spaces/${spaceId}/content`);
  };

  if (!article) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="flex flex-col h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to={`/spaces/${spaceId}/content`} className="text-zinc-500 hover:text-zinc-900" data-testid="back-to-content">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <input value={article.title} onChange={(e) => setField("title", e.target.value)}
          data-testid="article-title-edit"
          className="flex-1 text-lg font-bold tracking-tight bg-transparent focus:outline-none px-2 py-1 rounded-sm focus:bg-zinc-50" />
        <select value={article.status} onChange={(e) => setField("status", e.target.value)}
          data-testid="article-status-select"
          className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 border border-zinc-300 rounded-sm bg-white">
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <button onClick={() => save()} disabled={saving} data-testid="save-article-btn"
          className="border border-zinc-300 hover:bg-zinc-50 text-sm font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1.5 disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
        </button>
        {article.status === "draft" ? (
          <button onClick={() => save({ publish: true })} data-testid="publish-article-btn"
            className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold px-4 py-1.5 rounded-sm text-sm">Publish</button>
        ) : (
          <button onClick={() => save({ unpublish: true })}
            className="border border-zinc-300 hover:bg-zinc-50 text-sm font-semibold px-3 py-1.5 rounded-sm">Unpublish</button>
        )}
        <DropMenu duplicate={duplicate} deleteArticle={deleteArticle} viewVersions={() => setShowVersions(true)} />
      </header>

      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            {/* Lang tabs */}
            <div className="flex items-center gap-2 mb-4 border-b border-zinc-200 pb-2">
              <button onClick={() => switchLang("en")}
                data-testid="lang-tab-en"
                className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                  activeLang === "en" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"
                }`}>
                🇺🇸 English {dirty && activeLang === "en" ? "•" : ""}
              </button>
              <button onClick={() => switchLang("ar")}
                data-testid="lang-tab-ar"
                className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                  activeLang === "ar" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"
                }`}>
                🇸🇦 العربية {dirty && activeLang === "ar" ? "•" : ""}
              </button>
              {activeLang === "ar" && (
                <button onClick={copyEnToAr}
                  data-testid="copy-en-to-ar"
                  className="ml-auto text-xs font-semibold text-[#41B5E6] hover:underline">
                  ⤴ Copy from English
                </button>
              )}
            </div>

            {activeLang === "en" ? (
              <EditorJsEditor
                editorKey={`en-${articleId}-${editorNonce}`}
                flushRef={editorFlushRef}
                value={content}
                onChange={(v) => { setContent(v); setDirty(true); }}
                onImageUpload={onImageUpload}
                dir="ltr"
                placeholder="Start writing your article…"
              />
            ) : (
              <div dir="rtl" className="text-right">
                <div className="mb-3">
                  <label className="block text-sm font-bold mb-1.5">العنوان (Arabic title)</label>
                  <input value={titleAr} onChange={(e) => { setTitleAr(e.target.value); setDirty(true); }}
                    data-testid="ar-title-input"
                    className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-base font-bold text-right focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-bold mb-1.5">الملخص (Arabic excerpt)</label>
                  <textarea value={excerptAr} onChange={(e) => { setExcerptAr(e.target.value); setDirty(true); }} rows={2}
                    data-testid="ar-excerpt-input"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#41B5E6]" />
                </div>
                <label className="block text-sm font-bold mb-1.5">المحتوى (Arabic content)</label>
                <EditorJsEditor
                  editorKey={`ar-${articleId}-${editorNonce}`}
                  flushRef={editorFlushRef}
                  value={contentAr}
                  onChange={(v) => { setContentAr(v); setDirty(true); }}
                  onImageUpload={onImageUpload}
                  dir="rtl"
                  placeholder="ابدأ بكتابة المقال…"
                />
              </div>
            )}
          </div>
        </main>

        {showSidebar && (
          <aside className="w-80 bg-white border-l border-zinc-200 overflow-y-auto p-5 space-y-5">
            <Field label="Category">
              <select value={article.category_id || ""} onChange={(e) => setField("category_id", e.target.value || null)}
                data-testid="article-category-select"
                className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm">
                <option value="">— Uncategorized —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </Field>
            <Field label="Slug">
              <input value={article.slug} onChange={(e) => setField("slug", e.target.value)}
                className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm font-mono" />
            </Field>
            <Field label="Tags (comma separated)">
              <input value={tagInput} onChange={(e) => { setTagInput(e.target.value); setDirty(true); }}
                placeholder="onboarding, billing"
                className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm" />
            </Field>
            <Field label="Excerpt">
              <textarea value={article.excerpt || ""} onChange={(e) => setField("excerpt", e.target.value)}
                rows={3} placeholder="Short summary…"
                className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm" />
            </Field>
            <Field label="Featured image">
              <div className="flex items-center gap-2">
                {article.featured_image_url && (
                  <img src={article.featured_image_url} alt="cover" className="h-12 w-20 object-cover rounded-sm border" />
                )}
                <label className="text-xs border border-zinc-300 hover:bg-zinc-50 px-2.5 py-1.5 rounded-sm cursor-pointer">
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const url = await onImageUpload(f);
                    setField("featured_image_url", url);
                  }} />
                </label>
              </div>
            </Field>
            <div className="border-t pt-4">
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-3">SEO</div>
              <Field label="Meta title">
                <input value={article.seo?.meta_title || ""} onChange={(e) => setField("seo", { ...article.seo, meta_title: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm" />
              </Field>
              <Field label="Meta description">
                <textarea value={article.seo?.meta_description || ""} onChange={(e) => setField("seo", { ...article.seo, meta_description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm" />
              </Field>
            </div>
            <div className="text-xs text-zinc-500 pt-3 border-t">
              <div>{article.reading_time_minutes} min read</div>
              <div>Updated {new Date(article.updated_at).toLocaleString()}</div>
            </div>
          </aside>
        )}
      </div>

      {showVersions && (
        <VersionsModal spaceId={spaceId} articleId={articleId} onClose={() => setShowVersions(false)} onRestored={load} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function DropMenu({ duplicate, deleteArticle, viewVersions }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid="article-menu-btn">
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-white border border-zinc-200 rounded-sm shadow-md py-1 z-50 w-48">
            <button onClick={() => { viewVersions(); setOpen(false); }} className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-zinc-50 text-left">
              <History className="w-3.5 h-3.5" /> View versions
            </button>
            <button onClick={() => { duplicate(); setOpen(false); }} className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-zinc-50 text-left">
              <Copy className="w-3.5 h-3.5" /> Duplicate
            </button>
            <button onClick={() => { deleteArticle(); setOpen(false); }} className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-rose-50 text-rose-600 text-left">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function VersionsModal({ spaceId, articleId, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    api.get(`/spaces/${spaceId}/articles/${articleId}/versions`).then((r) => setVersions(r.data));
  }, [spaceId, articleId]);

  const restore = async (vid) => {
    if (!confirm("Restore this version? Current content will be replaced.")) return;
    await api.put(`/spaces/${spaceId}/articles/${articleId}/versions/${vid}/restore`);
    toast.success("Restored");
    onRestored();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-sm max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-bold">Version history</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <div className="divide-y divide-zinc-100">
          {versions.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500 text-center">No previous versions yet.</div>
          ) : versions.map((v) => (
            <div key={v.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">v{v.version_number} — {v.title}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{new Date(v.created_at).toLocaleString()}</div>
              </div>
              <button onClick={() => restore(v.id)} className="text-xs border border-zinc-300 hover:bg-zinc-50 px-3 py-1.5 rounded-sm font-semibold">Restore</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
